import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import API from '../../../services/API'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import PermissionCheck from '../../../helpers/PermissionCheck'
import BigModal from '../../../components/Modals/BigModal'
import EvidenceStatusChip from '../components/EvidenceStatusChip'
import AddEvidence from './AddEvidence'

const columns = (onDelete) => [
  { field: 'title', headerName: 'Evidence', flex: 1.4 },
  { field: 'keyQuestion', headerName: 'Key Question', flex: 0.8 },
  { field: 'evidenceCategory', headerName: 'Category', flex: 1 },
  {
    field: 'evidenceDate',
    headerName: 'Evidence Date',
    flex: 0.8,
    valueFormatter: (value) => (value ? moment(value).format('Do MMM YYYY') : ''),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.7,
    renderCell: (params) => <EvidenceStatusChip status={params.value} />,
  },
  {
    field: 'uploadedManually',
    headerName: 'Source',
    flex: 0.7,
    valueFormatter: (value) => (value ? 'Manual upload' : 'Automatic'),
  },
  {
    field: 'actions',
    headerName: '',
    flex: 0.4,
    sortable: false,
    renderCell: (params) =>
      params.row.uploadedManually ? (
        <DeleteIcon
          color="error"
          fontSize="small"
          sx={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation()
            onDelete(params.row._id)
          }}
        />
      ) : null,
  },
]

// Evidence rows that point at a record with its own page deep-link to it
const SOURCE_ROUTE = {
  risk: (sourceId) => `/risk-register/${sourceId}`,
  complianceQuestion: () => '/cqc-centre/compliance',
}

const EvidenceLibrary = () => {
  const [evidenceItems, setEvidenceItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [openAdd, setOpenAdd] = useState(false)
  const navigate = useNavigate()

  const loadEvidence = () => {
    setLoading(true)
    API.get('/cqc-centre/evidence?limit=200')
      .then((res) => {
        if (res.status === 200) setEvidenceItems(res.data.evidenceItems)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadEvidence()
  }, [])

  const handleDelete = (evidenceID) => {
    if (!window.confirm('Delete this evidence item?')) return
    API.post('/cqc-centre/evidence/delete', { evidenceID }).then((res) => {
      if (res.status === 200) loadEvidence()
    })
  }

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="title1">Evidence Library</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)}>
            Add Evidence
          </Button>
        </Stack>
        <Alert severity="info" variant="outlined">
          Most evidence here is collected automatically from your Training, DBS, Governance, Fleet
          and other records. Use "Add Evidence" for anything that doesn't have a natural home
          elsewhere in AmbuNet, e.g. a Statement of Purpose or a partner assurance letter.
        </Alert>
        <div style={{ height: 600, width: '100%' }}>
          <CustomDataGrid
            rows={evidenceItems}
            columns={columns(handleDelete)}
            getRowId={(row) => row._id}
            loading={loading}
            color="anBlue"
            onRowClick={(params) => {
              const route = SOURCE_ROUTE[params.row.sourceType]
              if (route) navigate(route(params.row.sourceId))
            }}
          />
        </div>
      </Stack>
      <BigModal open={openAdd} onClose={() => setOpenAdd(false)}>
        <AddEvidence
          onClose={() => setOpenAdd(false)}
          onSaved={() => {
            setOpenAdd(false)
            loadEvidence()
          }}
        />
      </BigModal>
    </PermissionCheck>
  )
}

export default EvidenceLibrary
